import { SyntheticEvent, useEffect, useState } from 'react';
import { useRecoilState } from 'recoil';

import Checkbox from '@/components/atoms/Checkbox';
import NoPermissionText from '@/components/atoms/NoPermissionText';
import BasicTabs from '@/components/molecules/BasicTabs';
import EmployeeSearchCard from '@/components/molecules/EmployeeSearchCard';
import NewSideFilter from '@/components/molecules/NewSideFilter';
import { TurnstileAccessHeader } from '@/components/molecules/TurnstileAccessHeader';
import TurnstileAccessTab from '@/components/organisms/TurnstileAccessTab';
import { Label } from '@/components/ui/label';
import { filters } from '@/constants/filters/TurnstileAccessFilter';
import { TurnstileAccessType } from '@/constants/TurnstileAccessConstants';
import { useTurnstileAccessData } from '@/hooks/useTurnstileAccessData';
import { turnstileAccessFilterAtom } from '@/state/TurnstileAccessFilter.atom';
import {
	userCanRunTurnstileRoutine,
	userCanToggleShowAllTurnstileAccess,
} from '@/utils/handleSavePermissions';

const TurnstileAccess = () => {
	const [searchInput, setSearchInput] = useState('');
	const [filteredData, setFilteredData] = useRecoilState(
		turnstileAccessFilterAtom,
	);

	const {
		// Filter data
		filterOptions,
		isLoadingFilters,

		// Blocked data
		blockedEmployees,
		blockedCount,
		isLoadingBlocked,
		fetchNextPageBlocked,
		isFetchingNextPageBlocked,
		hasNextPageBlocked,

		// In risk data
		inRiskEmployees,
		inRiskCount,
		isLoadingInRisk,
		fetchNextPageInRisk,
		isFetchingNextPageInRisk,
		hasNextPageInRisk,

		// Exception data
		exceptionEmployees,
		exceptionCount,
		isLoadingException,
		fetchNextPageException,
		isFetchingNextPageException,
		hasNextPageException,
	} = useTurnstileAccessData({ filteredData, searchInput });

	const handleInputChange = (name: string) => {
		setSearchInput(name);
	};

	const handleTabChange = (event: SyntheticEvent, newValue: number) => {
		if (newValue === 0) {
			setFilteredData({
				...filteredData,
				type: TurnstileAccessType.Blocked,
			});
		} else if (newValue === 1) {
			setFilteredData({
				...filteredData,
				type: TurnstileAccessType.InRisk,
			});
		} else {
			setFilteredData({
				...filteredData,
				type: TurnstileAccessType.Exception,
			});
		}
	};

	const customResetFilters = () => {
		setFilteredData((prev) => ({
			board: [],
			coordination: [],
			employee: [],
			employeeStatus: [],
			jobTitle: [],
			management: [],
			pole: [],
			training: [],
			workstation: [],
			type: prev.type,
			showAllEmployees: prev.showAllEmployees,
		}));
	};

	const getInitialTab = () => {
		switch (filteredData.type) {
			case TurnstileAccessType.Blocked:
				return 0;
			case TurnstileAccessType.InRisk:
				return 1;
			case TurnstileAccessType.Exception:
				return 2;
			default:
				return 0;
		}
	};

	const isShowAllDisabled = !userCanToggleShowAllTurnstileAccess();
	const canUserRunRoutine = userCanRunTurnstileRoutine();

	useEffect(() => {
		if (
			isShowAllDisabled &&
			canUserRunRoutine &&
			!filteredData.showAllEmployees
		) {
			setFilteredData((prev) => ({ ...prev, showAllEmployees: true }));
		}
	}, [
		isShowAllDisabled,
		canUserRunRoutine,
		filteredData.showAllEmployees,
		setFilteredData,
	]);

	return (
		<div className="relative flex h-full max-h-screen w-full flex-col items-start">
			<div className="absolute -top-10 right-20 flex items-center">
				<TurnstileAccessHeader />
			</div>
			<NewSideFilter
				filters={filters}
				atom={turnstileAccessFilterAtom}
				options={filterOptions}
				isLoading={isLoadingFilters}
				customResetFilters={customResetFilters}
			>
				<div className="my-1 flex items-center gap-2">
					<Checkbox
						defaultChecked={false}
						onChange={() =>
							setFilteredData((prev) => ({
								...prev,
								showAllEmployees: !prev.showAllEmployees,
							}))
						}
						checked={!!filteredData.showAllEmployees}
						disabled={isShowAllDisabled}
					/>
					<Label className="text-xs font-normal text-white">
						Mostrar todos os Colaboradores
					</Label>
				</div>
			</NewSideFilter>
			<div className="max-h-[85vh] w-full">
				<BasicTabs
					initialTab={getInitialTab()}
					background="transparent"
					tabsNames={[
						'Bloqueados nos Polos',
						'Em risco de bloqueio nos Polos',
						'Exceção de Bloqueio nos Polos',
					]}
					onChange={handleTabChange}
					tabsCounts={[blockedCount, inRiskCount, exceptionCount]}
					tabs={[
						<div key={0} className="flex w-full min-w-min flex-col gap-2 pt-2">
							<EmployeeSearchCard
								searchInput={searchInput}
								handleInputChange={handleInputChange}
							/>
							<TurnstileAccessTab
								isLoading={isLoadingBlocked}
								employees={blockedEmployees}
								isFetchingNextPage={isFetchingNextPageBlocked}
								hasNextPage={hasNextPageBlocked}
								fetchNextPage={fetchNextPageBlocked}
							/>
						</div>,
						<div key={1} className="flex w-full min-w-min flex-col gap-2 pt-2">
							<EmployeeSearchCard
								searchInput={searchInput}
								handleInputChange={handleInputChange}
							/>
							<TurnstileAccessTab
								isLoading={isLoadingInRisk}
								employees={inRiskEmployees}
								isFetchingNextPage={isFetchingNextPageInRisk}
								hasNextPage={hasNextPageInRisk}
								fetchNextPage={fetchNextPageInRisk}
							/>
						</div>,
						<div key={2} className="flex w-full min-w-min flex-col gap-2 pt-2">
							<EmployeeSearchCard
								searchInput={searchInput}
								handleInputChange={handleInputChange}
							/>
							<TurnstileAccessTab
								isLoading={isLoadingException}
								employees={exceptionEmployees}
								isFetchingNextPage={isFetchingNextPageException}
								hasNextPage={hasNextPageException}
								fetchNextPage={fetchNextPageException}
								isExceptionTab
							/>
						</div>,
					]}
				/>
			</div>
		</div>
	);
};

export default TurnstileAccess;
